import { createContext, useContext, useEffect, useState } from "react";
import memberstackDOM from "@memberstack/dom";

/**
 * Singleton Memberstack DOM instance + React context.
 * Exposes the SDK, the current member, and auth state.
 *
 * Memberstack handles auth + Stripe billing entirely on the client side via cookies.
 * No data is persisted on our backend — same privacy ethos as the rest of AWAKE.
 */
const MemberstackContext = createContext(null);

let _msInstance = null;

function getMemberstack() {
  if (_msInstance) return _msInstance;
  const publicKey = process.env.REACT_APP_MEMBERSTACK_PUBLIC_KEY;
  if (!publicKey) {
    // eslint-disable-next-line no-console
    console.error("REACT_APP_MEMBERSTACK_PUBLIC_KEY is missing");
    return null;
  }

  // Use the Memberstack custom-domain cookie endpoint when running on a
  // wearetheweave.co.uk subdomain. This makes the auth cookie scope `.wearetheweave.co.uk`,
  // so a paid member signed in on awake.* is also recognised on loom.*, etc.
  // On Preview / localhost we let Memberstack use its default domain (no extra config).
  const initConfig = { publicKey, useCookies: true };
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host.endsWith(".wearetheweave.co.uk") || host === "wearetheweave.co.uk") {
      initConfig.domain = "https://memberstack-client.wearetheweave.co.uk";
    }
  }

  _msInstance = memberstackDOM.init(initConfig);
  return _msInstance;
}

export function MemberstackProvider({ children }) {
  const [ms] = useState(() => getMemberstack());
  const [currentMember, setCurrentMember] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initial fetch + subscribe to auth changes
  useEffect(() => {
    if (!ms) {
      setLoading(false);
      return;
    }

    let unsubscribe = () => {};
    let mounted = true;

    ms.getCurrentMember()
      .then(({ data }) => {
        if (!mounted) return;
        setCurrentMember(data || null);
      })
      .catch(() => {
        if (!mounted) return;
        setCurrentMember(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    try {
      const listener = ms.onAuthChange((member) => {
        if (!mounted) return;
        setCurrentMember(member || null);
      });
      if (listener && typeof listener.unsubscribe === "function") {
        unsubscribe = () => listener.unsubscribe();
      }
    } catch (_) {
      // onAuthChange not available in some SDK versions — getCurrentMember covers us
    }

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [ms]);

  const value = {
    memberstack: ms,
    currentMember,
    isAuthenticated: !!currentMember,
    loading,
    refreshMember: async () => {
      if (!ms) return;
      const { data } = await ms.getCurrentMember();
      setCurrentMember(data || null);
    },
  };

  return (
    <MemberstackContext.Provider value={value}>
      {children}
    </MemberstackContext.Provider>
  );
}

export function useMemberstack() {
  const ctx = useContext(MemberstackContext);
  if (!ctx) {
    throw new Error("useMemberstack must be used inside <MemberstackProvider>");
  }
  return ctx;
}

/**
 * Helper: extract a friendly first name from a Memberstack member object.
 * Members can store firstName in customFields, or fall back to the email local part.
 */
export function getFirstName(member) {
  if (!member) return "";
  const fromCustom =
    member.customFields?.firstName ||
    member.customFields?.["first-name"] ||
    member.customFields?.["first_name"];
  if (fromCustom) return String(fromCustom).trim();
  const email = member.auth?.email || member.email || "";
  if (email.includes("@")) return email.split("@")[0];
  return "";
}

/**
 * Helper: get a list of human-readable plan names from a member object.
 */
export function getPlanLabels(member) {
  if (!member?.planConnections?.length) return [];
  return member.planConnections
    .filter((p) => p.active !== false && p.status !== "CANCELED")
    .map((p) => p.planName || p.planId)
    .filter(Boolean);
}

/**
 * Helper: is this member a Weave team admin?
 * Reads a "role" custom field on the Memberstack member. Founders/cofounders
 * are tagged in the Memberstack dashboard with one of:
 *   - customFields.role === "admin"
 *   - customFields.admin === true (or "true" / "yes")
 * Returns false for guests, free members, and paid 1% Club members alike.
 */
export function isAdmin(member) {
  if (!member) return false;
  const cf = member.customFields || {};
  if (typeof cf.role === "string" && cf.role.toLowerCase() === "admin") return true;
  const adminFlag = cf.admin;
  if (adminFlag === true) return true;
  if (typeof adminFlag === "string") {
    const v = adminFlag.toLowerCase().trim();
    if (v === "true" || v === "yes" || v === "1") return true;
  }
  return false;
}
